import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';
import SupplierList from './SupplierList';
import SupplierForm from './SupplierForm';
import axios from 'axios';

// API URL configuration - uses relative URL in production (nginx proxies to backend)
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use(config => {
  const token = safeStorage.getItem('authToken');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

const SupplierScreen = () => {
  const { isReadOnly } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({
    supplier_name: '',
    supplier_address: '',
    supplier_contact_info: ''
  });

  const readOnlyMode = isReadOnly('/suppliers');

  const fetchSuppliers = async () => {
    const params = {};
    Object.keys(filters).forEach(key => {
      if (filters[key]) params[key] = filters[key];
    });
    const res = await api.get('/suppliers', { params });
    setSuppliers(res.data);
  };

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line
  }, [filters]);

  const handleCreate = () => {
    if (readOnlyMode) return;
    setEditingSupplier(null);
    setShowForm(true);
  };

  const handleEdit = supplier => {
    if (readOnlyMode) return;
    setEditingSupplier(supplier);
    setShowForm(true);
  };

  const handleDelete = async id => {
    if (readOnlyMode) return;
    await api.delete(`/suppliers/${id}`);
    fetchSuppliers();
  };

  const handleSave = async formData => {
    if (editingSupplier) {
      await api.put(`/suppliers/${editingSupplier.supplier_id}`, formData);
    } else {
      await api.post('/suppliers', formData);
    }
    fetchSuppliers();
    setShowForm(false);
    setEditingSupplier(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSupplier(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters(f => ({ ...f, [field]: value }));
  };

  return (
    <div>
      <h1>Suppliers</h1>
      {!readOnlyMode && (
        <button onClick={handleCreate}>Create New Supplier</button>
      )}
      {readOnlyMode && (
        <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '1rem' }}>
          Read-only mode: You can view but not modify suppliers
        </p>
      )}
      {showForm && !readOnlyMode && (
        <SupplierForm
          supplier={editingSupplier}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
      <SupplierList
        suppliers={suppliers}
        filters={filters}
        onFilterChange={handleFilterChange}
        onEdit={readOnlyMode ? null : handleEdit}
        onDelete={readOnlyMode ? null : handleDelete}
        readOnly={readOnlyMode}
      />
    </div>
  );
};

export default SupplierScreen;